import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import {
  mdiAlertCircle,
  mdiCheck,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiDownload,
  mdiLoading,
  mdiPlay,
} from '@mdi/js';
import * as React from 'react';
import {
  EventReportResult,
  ReportFilters,
  useExportCsv,
  useGenerateReport,
  useReportOptions,
} from './hooks/useEventReport';

// Human-readable labels for event types
const eventTypeLabels: Record<string, string> = {
  plan_created: 'Plan Created',
  plan_deleted: 'Plan Deleted',
  review_submitted: 'Review Submitted',
  review_edited: 'Review Edited',
  business_viewed: 'Business Viewed',
  business_favorited: 'Business Favorited',
  business_unfavorited: 'Business Unfavorited',
  business_created: 'Business Created',
  user_registered: 'User Registered',
  filter_selected: 'Filter Selected',
  category_selected: 'Category Selected',
  search_performed: 'Search Performed',
};

// Human-readable labels for groupBy options
const groupByLabels: Record<string, string> = {
  user: 'User',
  business: 'Business',
  date: 'Date',
  source: 'Source',
};

// Human-readable labels for entity types
const entityTypeLabels: Record<string, string> = {
  business: 'Business',
  plan: 'Plan',
  review: 'Review',
  user: 'User',
  favorite: 'Favorite',
  import_job: 'Import Job',
};

// Get today and start of month as defaults
const getDefaultDates = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: startOfMonth.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  };
};

export const EventReportPage: React.FC = () => {
  const { data: options, isLoading: optionsLoading } = useReportOptions();
  const generateReport = useGenerateReport();
  const exportCsv = useExportCsv();

  const defaults = getDefaultDates();

  // Form state
  const [startDate, setStartDate] = React.useState(defaults.startDate);
  const [endDate, setEndDate] = React.useState(defaults.endDate);
  const [selectedEventTypes, setSelectedEventTypes] = React.useState<string[]>(
    [],
  );
  const [selectedGroupBy, setSelectedGroupBy] = React.useState<string[]>([
    'user',
    'business',
    'date',
  ]);

  // Optional filters state
  const [selectedEntityTypes, setSelectedEntityTypes] = React.useState<
    string[]
  >([]);
  const [businessIdFilter, setBusinessIdFilter] = React.useState<string>('');
  const [userIdFilter, setUserIdFilter] = React.useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  // Report state
  const [report, setReport] = React.useState<EventReportResult | null>(null);

  // Filter client events (exclude admin events)
  const clientEventTypes = React.useMemo(() => {
    if (!options?.eventTypes) return [];
    return options.eventTypes.filter(type => !type.startsWith('admin_'));
  }, [options?.eventTypes]);

  // Toggle event type selection
  const toggleEventType = (eventType: string) => {
    setSelectedEventTypes(prev =>
      prev.includes(eventType)
        ? prev.filter(t => t !== eventType)
        : [...prev, eventType],
    );
  };

  // Select/deselect all event types
  const selectAllEventTypes = () => {
    setSelectedEventTypes(clientEventTypes);
  };

  const clearEventTypes = () => {
    setSelectedEventTypes([]);
  };

  // Toggle groupBy selection
  const toggleGroupBy = (group: string) => {
    setSelectedGroupBy(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group],
    );
  };

  // Toggle entity type selection
  const toggleEntityType = (entityType: string) => {
    setSelectedEntityTypes(prev =>
      prev.includes(entityType)
        ? prev.filter(t => t !== entityType)
        : [...prev, entityType],
    );
  };

  // Build filters object with optional filters
  const buildFilters = (): ReportFilters => {
    const filters: ReportFilters = {
      startDate,
      endDate,
      eventTypes: selectedEventTypes,
      groupBy: selectedGroupBy,
    };

    // Add optional filters if set
    if (selectedEntityTypes.length > 0) {
      filters.entityTypes = selectedEntityTypes;
    }
    if (businessIdFilter.trim()) {
      const parsed = parseInt(businessIdFilter, 10);
      if (!isNaN(parsed)) {
        filters.businessId = parsed;
      }
    }
    if (userIdFilter.trim()) {
      const parsed = parseInt(userIdFilter, 10);
      if (!isNaN(parsed)) {
        filters.userId = parsed;
      }
    }

    return filters;
  };

  // Run report
  const handleRunReport = async () => {
    if (selectedEventTypes.length === 0) {
      return;
    }
    if (selectedGroupBy.length === 0) {
      return;
    }

    const filters = buildFilters();
    const result = await generateReport.mutateAsync(filters);
    setReport(result);
  };

  // Export CSV
  const handleExportCsv = async () => {
    if (!report) return;
    const filters = buildFilters();
    await exportCsv.mutateAsync(filters);
  };

  // Get column headers for the results table
  const getColumnHeaders = (): string[] => {
    const headers: string[] = [];

    if (selectedGroupBy.includes('user')) {
      headers.push('User');
    }
    if (selectedGroupBy.includes('business')) {
      headers.push('Business');
    }
    if (selectedGroupBy.includes('date')) {
      headers.push('Date');
    }
    if (selectedGroupBy.includes('source')) {
      headers.push('Source');
    }

    // Add event type columns
    headers.push(...selectedEventTypes.map(t => eventTypeLabels[t] || t));

    return headers;
  };

  // Get cell value for a row
  const getCellValue = (
    row: Record<string, unknown>,
    header: string,
  ): string | number => {
    if (header === 'User') {
      return (row.userName as string) || '-';
    }
    if (header === 'Business') {
      return (row.businessName as string) || '-';
    }
    if (header === 'Date') {
      return (row.date as string) || '-';
    }
    if (header === 'Source') {
      return (row.source as string) || '-';
    }

    // Find the event type for this header
    const eventType = Object.entries(eventTypeLabels).find(
      ([, label]) => label === header,
    )?.[0];

    if (eventType && row[eventType] !== undefined) {
      return row[eventType] as number;
    }

    // Try direct match (for event types without labels)
    if (row[header] !== undefined) {
      return row[header] as number;
    }

    return 0;
  };

  if (optionsLoading) {
    return (
      <DashboardLayout>
        <ProtectedRoute action='read' subject='UserEvent'>
          <div className='flex items-center justify-center min-h-96'>
            <Icon
              path={mdiLoading}
              size={32}
              className='text-grey-400 animate-spin'
            />
          </div>
        </ProtectedRoute>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ProtectedRoute action='read' subject='UserEvent'>
        <div className='space-y-6'>
          {/* Header */}
          <div>
            <h1 className='text-3xl font-bold text-grey-900'>Event Report</h1>
            <p className='text-base text-grey-600 mt-2'>
              Query and export event data for analysis
            </p>
          </div>

          {/* Filters Card */}
          <CardBox>
            <div className='space-y-6'>
              {/* Date Range */}
              <div>
                <h3 className='text-sm font-semibold text-grey-900 mb-3'>
                  Date Range
                </h3>
                <div className='flex items-center gap-4'>
                  <div>
                    <label className='block text-xs text-grey-500 mb-1'>
                      From
                    </label>
                    <input
                      type='date'
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className='px-3 py-2 border border-grey-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffice-orange/50 focus:border-coffice-orange'
                    />
                  </div>
                  <div>
                    <label className='block text-xs text-grey-500 mb-1'>
                      To
                    </label>
                    <input
                      type='date'
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className='px-3 py-2 border border-grey-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffice-orange/50 focus:border-coffice-orange'
                    />
                  </div>
                </div>
              </div>

              {/* Event Types */}
              <div>
                <div className='flex items-center justify-between mb-3'>
                  <h3 className='text-sm font-semibold text-grey-900'>
                    Event Types
                  </h3>
                  <div className='flex gap-2'>
                    <button
                      onClick={selectAllEventTypes}
                      className='text-xs text-coffice-orange hover:text-coffice-orange/80'
                    >
                      Select All
                    </button>
                    <span className='text-grey-300'>|</span>
                    <button
                      onClick={clearEventTypes}
                      className='text-xs text-grey-500 hover:text-grey-700'
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {clientEventTypes.map(eventType => (
                    <button
                      key={eventType}
                      onClick={() => toggleEventType(eventType)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        selectedEventTypes.includes(eventType)
                          ? 'bg-coffice-orange/10 text-grey-900'
                          : 'bg-grey-50 text-grey-600 hover:bg-grey-100'
                      }`}
                    >
                      <Icon
                        path={
                          selectedEventTypes.includes(eventType)
                            ? mdiCheckboxMarked
                            : mdiCheckboxBlankOutline
                        }
                        size={18}
                        className={
                          selectedEventTypes.includes(eventType)
                            ? 'text-coffice-orange'
                            : 'text-grey-400'
                        }
                      />
                      {eventTypeLabels[eventType] || eventType}
                    </button>
                  ))}
                </div>
                {selectedEventTypes.length === 0 && (
                  <p className='text-xs text-red-500 mt-2 flex items-center gap-1'>
                    <Icon path={mdiAlertCircle} size={14} />
                    Select at least one event type
                  </p>
                )}
              </div>

              {/* Group By */}
              <div>
                <h3 className='text-sm font-semibold text-grey-900 mb-3'>
                  Group By
                </h3>
                <div className='flex flex-wrap gap-2'>
                  {options?.groupByOptions.map(group => (
                    <button
                      key={group}
                      onClick={() => toggleGroupBy(group)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedGroupBy.includes(group)
                          ? 'bg-grey-900 text-white'
                          : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                      }`}
                    >
                      {selectedGroupBy.includes(group) && (
                        <Icon path={mdiCheck} size={16} />
                      )}
                      {groupByLabels[group] || group}
                    </button>
                  ))}
                </div>
                {selectedGroupBy.length === 0 && (
                  <p className='text-xs text-red-500 mt-2 flex items-center gap-1'>
                    <Icon path={mdiAlertCircle} size={14} />
                    Select at least one grouping option
                  </p>
                )}
              </div>

              {/* Advanced Filters Toggle */}
              <div>
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className='text-sm text-coffice-orange hover:text-coffice-orange/80 font-medium'
                >
                  {showAdvancedFilters ? '− Hide' : '+ Show'} Advanced Filters
                </button>
              </div>

              {/* Advanced Filters */}
              {showAdvancedFilters && (
                <div className='space-y-4 p-4 bg-grey-50 rounded-lg'>
                  {/* Entity Type Filter */}
                  <div>
                    <h3 className='text-sm font-semibold text-grey-900 mb-3'>
                      Filter by Entity Type
                      <span className='text-grey-400 font-normal ml-2'>
                        (optional)
                      </span>
                    </h3>
                    <div className='flex flex-wrap gap-2'>
                      {options?.entityTypes.map(entityType => (
                        <button
                          key={entityType}
                          onClick={() => toggleEntityType(entityType)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                            selectedEntityTypes.includes(entityType)
                              ? 'bg-coffice-orange/10 text-grey-900'
                              : 'bg-white text-grey-600 hover:bg-grey-100 border border-grey-200'
                          }`}
                        >
                          <Icon
                            path={
                              selectedEntityTypes.includes(entityType)
                                ? mdiCheckboxMarked
                                : mdiCheckboxBlankOutline
                            }
                            size={18}
                            className={
                              selectedEntityTypes.includes(entityType)
                                ? 'text-coffice-orange'
                                : 'text-grey-400'
                            }
                          />
                          {entityTypeLabels[entityType] || entityType}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Specific ID Filters */}
                  <div className='flex gap-4'>
                    <div>
                      <label className='block text-sm font-semibold text-grey-900 mb-2'>
                        Filter by Business ID
                        <span className='text-grey-400 font-normal ml-2'>
                          (optional)
                        </span>
                      </label>
                      <input
                        type='number'
                        value={businessIdFilter}
                        onChange={e => setBusinessIdFilter(e.target.value)}
                        placeholder='e.g. 123'
                        className='px-3 py-2 border border-grey-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffice-orange/50 focus:border-coffice-orange w-32'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-grey-900 mb-2'>
                        Filter by User ID
                        <span className='text-grey-400 font-normal ml-2'>
                          (optional)
                        </span>
                      </label>
                      <input
                        type='number'
                        value={userIdFilter}
                        onChange={e => setUserIdFilter(e.target.value)}
                        placeholder='e.g. 456'
                        className='px-3 py-2 border border-grey-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffice-orange/50 focus:border-coffice-orange w-32'
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className='flex items-center gap-3 pt-4 border-t border-grey-200'>
                <button
                  onClick={handleRunReport}
                  disabled={
                    generateReport.isPending ||
                    selectedEventTypes.length === 0 ||
                    selectedGroupBy.length === 0
                  }
                  className='inline-flex items-center px-6 py-2 bg-grey-900 text-white rounded-lg text-sm font-medium hover:bg-grey-800 disabled:bg-grey-300 disabled:cursor-not-allowed transition-colors'
                >
                  {generateReport.isPending ? (
                    <Icon
                      path={mdiLoading}
                      size={18}
                      className='mr-2 animate-spin'
                    />
                  ) : (
                    <Icon path={mdiPlay} size={18} className='mr-2' />
                  )}
                  Run Report
                </button>
              </div>
            </div>
          </CardBox>

          {/* Results */}
          {generateReport.isError && (
            <CardBox className='bg-red-50 border-red-200'>
              <div className='flex items-center gap-2 text-red-700'>
                <Icon path={mdiAlertCircle} size={20} />
                <span>
                  Error:{' '}
                  {generateReport.error?.message || 'Failed to generate report'}
                </span>
              </div>
            </CardBox>
          )}

          {report && (
            <CardBox>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <h3 className='text-lg font-semibold text-grey-900'>
                    Results
                  </h3>
                  <p className='text-sm text-grey-500'>
                    {report.totalRows} rows
                  </p>
                </div>
                <button
                  onClick={handleExportCsv}
                  disabled={exportCsv.isPending || report.rows.length === 0}
                  className='inline-flex items-center px-4 py-2 bg-coffice-green text-grey-900 rounded-lg text-sm font-medium hover:bg-coffice-green/80 disabled:bg-grey-200 disabled:text-grey-400 disabled:cursor-not-allowed transition-colors'
                >
                  {exportCsv.isPending ? (
                    <Icon
                      path={mdiLoading}
                      size={18}
                      className='mr-2 animate-spin'
                    />
                  ) : (
                    <Icon path={mdiDownload} size={18} className='mr-2' />
                  )}
                  Export CSV
                </button>
              </div>

              {report.rows.length === 0 ? (
                <div className='text-center py-12 text-grey-500'>
                  No data found for the selected filters
                </div>
              ) : (
                <div className='overflow-x-auto'>
                  <table className='w-full'>
                    <thead>
                      <tr className='border-b border-grey-200'>
                        {getColumnHeaders().map(header => (
                          <th
                            key={header}
                            className='text-left py-3 px-4 text-sm font-medium text-grey-500 whitespace-nowrap'
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.slice(0, 100).map((row, index) => (
                        <tr
                          key={index}
                          className='border-b border-grey-50 hover:bg-grey-50'
                        >
                          {getColumnHeaders().map(header => (
                            <td
                              key={header}
                              className='py-3 px-4 text-sm text-grey-900 whitespace-nowrap'
                            >
                              {getCellValue(row, header)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {report.rows.length > 100 && (
                    <div className='text-center py-4 text-sm text-grey-500'>
                      Showing first 100 rows. Export CSV to see all{' '}
                      {report.totalRows} rows.
                    </div>
                  )}
                </div>
              )}
            </CardBox>
          )}
        </div>
      </ProtectedRoute>
    </DashboardLayout>
  );
};
